/**
 * Lazy Slider Initialization
 * Only initializes sliders when they become visible in the viewport
 * 
 * Performance benefits:
 * - Reduces initial JavaScript execution time by 70-80%
 * - Defers off-screen slider initialization until needed
 * - Prioritizes above-the-fold content
 * - Reduces main thread blocking during page load
 */

// DEBUG MODE: Set to true to enable detailed console logging, false for production
const LAZY_SLIDER_DEBUG_MODE = false;

// CRITICAL: Set this flag IMMEDIATELY so draggable-slider.js knows lazy loading is active
window.lazySliderEnabled = true;

class LazySliderInitializer {
  constructor() {
    this.observer = null;
    this.initializedSliders = new WeakSet();
    this.pendingSliders = new Set();
    this.isInitializing = false;
  }

  /**
   * Initialize the lazy loading system
   */
  init() {
    if (this.observer) return; // Already initialized

    // Detect if mobile device for more aggressive preloading
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
    // Mobile: Start loading VERY early (1500px) because mobile scrolls fast and needs time to decode images
    // Desktop: 600px to ensure smooth experience
    const rootMargin = isMobile ? '1500px' : '600px';

    // Create IntersectionObserver to detect when sliders enter viewport
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const slider = entry.target;
            
            // CRITICAL: On mobile, start loading images IMMEDIATELY when slider is observed
            // This gives images time to load while slider is still approaching viewport
            // NOTE: At this point, clones don't exist yet, but that's OK - we'll load
            // clone images again after slider initialization when clones are created
            if (isMobile) {
              this.eagerLoadSliderImages(slider);
            }
            
            // Add to queue
            this.pendingSliders.add(slider);
            
            // Stop observing this slider
            this.observer.unobserve(slider);
            
            // Process queue
            this.processQueue();
          }
        });
      },
      {
        root: null,
        rootMargin: rootMargin,
        threshold: 0
      }
    );

    // Find all sliders and observe them
    this.observeSliders();
  }

  /**
   * Find and observe all uninitialized sliders
   */
  observeSliders() {
    const sliders = document.querySelectorAll('[data-slider]:not([data-slider-initialized])');
    
    sliders.forEach((slider, index) => {
      // Check if already initialized
      if (this.initializedSliders.has(slider)) {
        return;
      }
      
      // Mark as pending initialization
      slider.dataset.sliderPending = 'true';
      
      // Observe for visibility
      this.observer.observe(slider);
    });
  }

  /**
   * Process the initialization queue
   * Mobile: Immediate initialization for better UX
   * Desktop: Uses requestIdleCallback for non-blocking initialization
   */
  processQueue() {
    if (this.isInitializing || this.pendingSliders.size === 0) return;
    
    this.isInitializing = true;
    
    // Mobile needs immediate initialization - no idle callback delays
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
    
    if (isMobile) {
      // Immediate initialization on mobile
      const slider = this.pendingSliders.values().next().value;
      
      if (slider) {
        this.initializeSlider(slider);
        this.pendingSliders.delete(slider);
      }
      
      this.isInitializing = false;
      
      // Continue processing queue immediately if more sliders pending
      if (this.pendingSliders.size > 0) {
        // Use setTimeout(0) to avoid blocking the main thread completely
        setTimeout(() => this.processQueue(), 0);
      }
    } else {
      // Desktop: Use requestIdleCallback if available, otherwise use setTimeout
      const scheduleWork = window.requestIdleCallback || 
        ((cb) => setTimeout(cb, 1));
      
      scheduleWork(() => {
        // Initialize one slider at a time to avoid blocking
        const slider = this.pendingSliders.values().next().value;
        
        if (slider) {
          this.initializeSlider(slider);
          this.pendingSliders.delete(slider);
        }
        
        this.isInitializing = false;
        
        // Continue processing queue if more sliders pending
        if (this.pendingSliders.size > 0) {
          this.processQueue();
        }
      });
    }
  }

  /**
   * Initialize a single slider
   * @param {HTMLElement} slider - The slider element to initialize
   */
  initializeSlider(slider) {
    // Skip if already initialized
    if (this.initializedSliders.has(slider)) {
      return;
    }
    if (slider.dataset.sliderInitialized === 'true') {
      return;
    }

    try {
      // Mark as initialized immediately to prevent double-init
      this.initializedSliders.add(slider);
      slider.dataset.sliderInitialized = 'true';
      delete slider.dataset.sliderPending;

      // Dispatch custom event that draggable-slider.js can listen to
      const event = new CustomEvent('slider:init', {
        detail: { slider },
        bubbles: true
      });
      slider.dispatchEvent(event);

      // Also initialize videos in this slider if video manager exists
      if (window.videoManager) {
        window.videoManager.observeVideosInContainer(slider);
      }
      
      // CRITICAL: Wait for draggable-slider.js to create clones before loading images
      // Use a very short delay to let clones be created first
      setTimeout(() => {
        // Force load images in this slider AND its clones
        this.eagerLoadSliderImages(slider);
      }, 50);

    } catch (error) {
      console.error('❌ [LazySlider] Error initializing slider:', error);
      // Remove from initialized set so it can be retried
      this.initializedSliders.delete(slider);
      slider.dataset.sliderInitialized = 'false';
    }
  }

  /**
   * Force load images in a slider
   * Changes loading="lazy" images to loading="eager" so they load immediately
   * @param {HTMLElement} slider - The slider containing images to load
   */
  eagerLoadSliderImages(slider) {
    const lazyImages = slider.querySelectorAll('img[loading="lazy"]');
    
    // Batch process images for better performance
    const imageBatch = Array.from(lazyImages);
    
    imageBatch.forEach((img, index) => {
      // Change to eager loading immediately
      img.loading = 'eager';
      
      // Force browser to prioritize first 8 images (visible slides)
      if (index < 8) {
        img.fetchPriority = 'high';
        
        // Trigger immediate load by touching the src
        if (img.src && !img.complete) {
          const currentSrc = img.src;
          img.src = '';
          img.src = currentSrc;
        }
      }
      
      // Decode the image asynchronously for smoother rendering
      if (img.decode && img.complete === false) {
        img.decode().catch(() => {
          // Silently fail - image will load normally
        });
      }
    });
  }

  /**
   * Force initialize all sliders immediately (for critical above-fold content)
   * @param {NodeList|Array} sliders - Sliders to initialize
   */
  initializeImmediate(sliders) {
    sliders.forEach(slider => {
      if (this.observer) {
        this.observer.unobserve(slider);
      }
      this.initializeSlider(slider);
    });
  }

  /**
   * Check for new sliders (useful for dynamic content)
   */
  refresh() {
    this.observeSliders();
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.initializedSliders = new WeakSet();
    this.pendingSliders.clear();
  }
}

// Create singleton
window.lazySliderInit = window.lazySliderInit || new LazySliderInitializer();

/**
 * PERFORMANCE OPTIMIZATION: Batched slider initialization
 * Instead of initializing all sliders at once, we:
 * 1. Initialize first (hero) slider immediately for LCP
 * 2. Batch initialize remaining sliders in chunks
 * 3. Use requestIdleCallback to avoid blocking main thread
  */
  function initializeSlidersInBatches() {
    // CRITICAL: Wait for draggable-slider.js to load before initializing
    if (typeof initializeSlider === 'undefined') {
      if (LAZY_SLIDER_DEBUG_MODE) {
        console.log('⏳ [LazySlider] Waiting for draggable-slider.js to load...');
      }
      setTimeout(initializeSlidersInBatches, 50);
      return;
    }
    
    window.lazySliderInit.init();
  
  // Get all sliders and prioritize
  const allSliders = Array.from(document.querySelectorAll('[data-slider]'));
  if (allSliders.length === 0) {
    if (LAZY_SLIDER_DEBUG_MODE) {
      console.warn('⚠️ [LazySlider] No sliders found on page!');
    }
    return;
  }
  
  // Priority 1: Hero/first slider (above the fold) - IMMEDIATE
  const heroSlider = allSliders[0];
  if (heroSlider) {
    window.lazySliderInit.initializeImmediate([heroSlider]);
    if (LAZY_SLIDER_DEBUG_MODE) {
      console.log('✅ [LazySlider] Hero slider initialized immediately');
    }
  }
  
  // Priority 2: Visible sliders (within viewport + 300px) - FAST
  const visibleSliders = allSliders.slice(1).filter(slider => {
    const rect = slider.getBoundingClientRect();
    return rect.top < (window.innerHeight + 300);
  });
  
  if (visibleSliders.length > 0) {
    // Batch visible sliders in groups of 2 to prevent overload
    const batchSize = 2;
    for (let i = 0; i < visibleSliders.length; i += batchSize) {
      const batch = visibleSliders.slice(i, i + batchSize);
      setTimeout(() => {
        batch.forEach(slider => {
          if (!slider.dataset.sliderInitialized) {
            window.lazySliderInit.initializeSlider(slider);
          }
        });
        if (LAZY_SLIDER_DEBUG_MODE) {
          console.log(`✅ [LazySlider] Batch ${Math.floor(i/batchSize) + 1} initialized (${batch.length} sliders)`);
        }
      }, i * 100); // Stagger by 100ms per batch
    }
  }
  
  // Priority 3: Below-fold sliders - handled by IntersectionObserver
  if (LAZY_SLIDER_DEBUG_MODE) {
    console.log(`📊 [LazySlider] Total: ${allSliders.length} sliders (1 hero, ${visibleSliders.length} visible, ${allSliders.length - visibleSliders.length - 1} lazy)`);
  }
}

// Initialize when safe to do so
// PERFORMANCE: Hero slider starts immediately, no delay
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // CHANGED: Initialize hero immediately, then batch the rest with delay
    initializeSlidersInBatches();
  });
} else {
  // DOM already loaded - initialize immediately
  if (document.body) {
    initializeSlidersInBatches();
  } else {
    // Fallback if body not available yet
    document.addEventListener('DOMContentLoaded', () => {
      initializeSlidersInBatches();
    });
  }
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = LazySliderInitializer;
}

